import FullCalendar from '@fullcalendar/react';
import { CalendarApi } from '@fullcalendar/core';
import { format as formatTz, toZonedTime } from 'date-fns-tz';
import { setHours, setMinutes, setSeconds, parse } from 'date-fns';

// Helper function to remove previously added axes
const removeAdditionalAxes = (calendarEl: HTMLElement | null) => {
  if (!calendarEl) return;
  calendarEl.querySelectorAll('.additional-tz-axis').forEach(el => el.remove());
};

// Main function to add additional timezone axes
export const addAdditionalTimezoneAxes = (
  calendarRef: React.RefObject<FullCalendar | null>,
  calendarWrapperRef: React.RefObject<HTMLDivElement | null>,
  additionalTimezones: string[],
  viewDate: Date | null // Pass the current view's reference date
) => {
  if (!calendarRef.current || !calendarWrapperRef.current || !viewDate) return;

  const calendarApi = calendarRef.current.getApi();
  const calendarEl = calendarWrapperRef.current;
  const mainTimezone = calendarApi.getOption('timeZone') || 'local';

  // 1. Cleanup previous axes
  removeAdditionalAxes(calendarEl);

  // --- Add Header Axes --- 
  const headerAxisSelectors = [
    '.fc-col-header thead .fc-axis', // Main header axis
    '.fc-timegrid-body .fc-col-header-cell:first-child' // For alignment maybe? Check structure
  ];
  const headerRows = calendarEl.querySelectorAll('.fc-col-header thead tr'); // Adjust if needed

  headerRows.forEach((headerRow: Element) => {
    const firstAxis = headerRow.querySelector('th.fc-timegrid-axis');
    console.log('[DOM Utils] Found header firstAxis:', firstAxis);
    if (!firstAxis) return;

    additionalTimezones.forEach((tzName, index) => {
      const newAxis = firstAxis.cloneNode(true) as HTMLElement;
      newAxis.classList.add('additional-tz-axis', `additional-tz-axis-${index}`);
      newAxis.style.width = '60px'; // Adjust width as needed
      newAxis.textContent = tzName.split('/').pop()?.replace('_', ' ') || tzName; // Display shorter name
      console.log(`[DOM Utils] Inserting header axis for ${tzName} after`, firstAxis);
      headerRow.appendChild(newAxis);
    });
  });

  // --- Add All-day Axes --- 
  const allDayRow = calendarEl.querySelector('.fc-scrollgrid-section-body .fc-daygrid-body tbody tr'); // More specific selector
  if (allDayRow) {
    const existingAxes = allDayRow.querySelectorAll('td.fc-timegrid-axis, td.additional-tz-axis-allday');
    const expectedTotalAxes = 1 + additionalTimezones.length; // 1 main + additional

    // Only add if axes are missing
    if (existingAxes.length < expectedTotalAxes) {
      const firstAllDayAxis = allDayRow.querySelector('td.fc-timegrid-axis'); // Re-find the reference axis
      if (firstAllDayAxis) {
        // Add only the missing axes
        const axesToAddCount = expectedTotalAxes - existingAxes.length;
        for (let i = 0; i < axesToAddCount; i++) {
          const tzIndex = additionalTimezones.length - axesToAddCount + i; // Get the correct timezone index
          if (tzIndex < 0) continue; // Should not happen, but safety check
          const tzName = additionalTimezones[tzIndex];

          const newAllDayAxis = firstAllDayAxis.cloneNode(true) as HTMLElement;
          newAllDayAxis.className = 'fc-timegrid-axis additional-tz-axis additional-tz-axis-allday'; // Reset className and add ours
          newAllDayAxis.style.width = '55px'; // Match other axes width
          newAllDayAxis.dataset.date = (firstAllDayAxis as HTMLElement).dataset.date || ''; // Copy data-date if exists
          
          // Clear the content of the inner cushion
          const innerCushion = newAllDayAxis.querySelector('.fc-timegrid-axis-cushion');
          if (innerCushion) {
            innerCushion.textContent = '\u00A0'; 
          }
          allDayRow.appendChild(newAllDayAxis);
          console.log(`[DOM Utils] Added missing all-day axis for ${tzName}`);
        }
      } else {
          console.warn('[DOM Utils] Could not find firstAllDayAxis for all-day row.');
      }
    }
  }

  // --- Add Body Axes --- 
  const slotRows = calendarEl.querySelectorAll('.fc-timegrid-slots table tbody tr');
  console.log(`[DOM Utils] Found ${slotRows.length} slot rows.`);
  
  slotRows.forEach((row: Element) => {
    const firstLabelCell = row.querySelector('td.fc-timegrid-slot-label');
    // Get time string from the label cell itself, not the row
    const timeStr = (firstLabelCell as HTMLElement)?.dataset.time;
    console.log(`[DOM Utils] Processing row for time ${timeStr}, found label cell:`, firstLabelCell);

    if (!firstLabelCell || !timeStr) return;

    // Attempt to parse the time string into a date object for today in the *local* browser context
    // Then we will convert it using the main timezone setting and then the target timezone.
    // This assumes the timeStr is relative to the start of the day.
    try {
      const [hours, minutes, seconds] = timeStr.split(':').map(Number);
      let baseDateForTime = setSeconds(setMinutes(setHours(viewDate, hours), minutes), seconds);

      additionalTimezones.forEach((tzName, index) => {
        const newLabelCell = firstLabelCell.cloneNode(true) as HTMLElement;
        newLabelCell.classList.add('additional-tz-axis', `additional-tz-axis-${index}`);
        newLabelCell.style.width = '60px'; // Match header width
        newLabelCell.style.textAlign = 'center';

        // Find the innermost element to set the text content, preserving parent structure
        const innerCushion = newLabelCell.querySelector('.fc-timegrid-slot-label-cushion');

        if (innerCushion) {
            innerCushion.textContent = ''; // Clear only the text content

            try {
              const zonedDate = toZonedTime(baseDateForTime, tzName);
              if (minutes === 0) { 
                 const formattedTime = formatTz(zonedDate, 'HH', { timeZone: tzName });
                 innerCushion.textContent = formattedTime; // Set text on the inner element
              } else {
                   innerCushion.textContent = ''; // Keep half-hour slots empty for now
              }
            } catch (e) {
              console.error(`Error formatting time ${timeStr} for timezone ${tzName}:`, e);
              innerCushion.textContent = 'Err';
            }
        } else {
             // Fallback if structure is different, might still cause issues
             newLabelCell.innerHTML = '&nbsp;'; 
             console.warn("[DOM Utils] Could not find inner cushion element for time label cell:", newLabelCell);
        }
        console.log(`[DOM Utils] Inserting body axis for ${tzName} at time ${timeStr} after`, firstLabelCell);
        row.appendChild(newLabelCell);
      });
    } catch (e) {
        console.error(`Error processing row with time ${timeStr}:`, e);
    }
  });

  // --- Add Content Column Axes (Crucial for alignment) ---
  const contentColRow = calendarEl.querySelector('.fc-timegrid-cols tbody tr');
  if (contentColRow) {
    const firstContentAxis = contentColRow.querySelector('td.fc-timegrid-axis'); // Find the axis cell in content cols
    if (firstContentAxis) {
      additionalTimezones.forEach((tzName, index) => {
        const newContentAxis = firstContentAxis.cloneNode(true) as HTMLElement; // Clone with content structure
        newContentAxis.classList.add('additional-tz-axis', `additional-tz-axis-${index}`)
        newContentAxis.style.width = '60px';
        newContentAxis.classList.add('additional-tz-axis-content'); 
        // Clear the content of the inner frame/cushion
        const innerFrame = newContentAxis.querySelector('.fc-timegrid-col-frame') || newContentAxis.querySelector('.fc-timegrid-axis-cushion'); // Find inner element
        if (innerFrame) {
            innerFrame.innerHTML = ''; // Clear inner HTML
        }
        contentColRow.appendChild(newContentAxis);
      });
    }
  }

  // Optional: Adjust overall width of containers if needed
  // This might require finding elements like .fc-view-harness and adjusting width/margin
  // Example (might need refinement):
  const viewContainer = calendarEl.querySelector('.fc-view-container');
  if (viewContainer) {
    // (viewContainer as HTMLElement).style.overflowX = 'auto'; // Allow horizontal scroll if needed
  }
}; 